import React, { useEffect, useState } from "react";
import { fetchDeploymentMode } from "../lib/api";
import { ShieldCheck, ShieldAlert, Server, HardDrive, Cloud, Radio } from "lucide-react";

const Pill = ({ on, label, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.15em] ${
      on ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
         : "bg-zinc-700/30 text-zinc-400 border border-zinc-600/40"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-400" : "bg-zinc-500"}`} />
    {label}
  </span>
);

/**
 * Compact horizontal banner shown at the top of the admin & agency consoles
 * so operators / auditors / inspectors know at a glance which mode the
 * system is in and which external services currently see their data.
 *
 * Renders nothing on the government-facing view by default. Operators
 * who want to inspect the deployment can re-enable it with the
 * ``rr_show_sov_banner = 1`` localStorage flag, or it auto-shows when
 * the deployment is NOT in sovereign mode (so a regression to a
 * non-Mali backend is visible immediately).
 *
 * Renders nothing while loading so it never causes layout jumps.
 */
export const SovereigntyBanner = () => {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchDeploymentMode()
      .then((d) => { if (alive) setInfo(d); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  if (err || !info) return null;
  const sovereign = info.mode === "sovereign";
  const forceShow = (() => {
    try { return localStorage.getItem("rr_show_sov_banner") === "1"; }
    catch { return false; }
  })();
  // Visible only when an operator has explicitly opted in via the
  // localStorage flag. The gov-facing console must stay free of
  // deployment-mode noise per the unified design system. Operators
  // who care (security auditors, infra) can still toggle the flag
  // from devtools to inspect.
  if (!forceShow) return null;
  // (sovereign still derived for the styling below — kept as the
  // visual switch between "all-green" and "amber-warning" looks.)
  void sovereign;
  const ext = info.external_services || {};
  const storage = info.storage || {};
  const runtime = info.runtime || {};
  // Multi-pod readiness lights up green when BOTH ws pub/sub and rate
  // limiting are running on Redis. In dev (REDIS_URL unset) it stays
  // amber — accurate signal for operators auditing the deployment.
  const multiPod = !!runtime.multi_pod_ready;

  return (
    <div
      data-testid="sovereignty-banner"
      className={`relative border-b text-[10px] font-bold uppercase tracking-[0.18em] ${
        sovereign
          ? "bg-emerald-950/60 text-emerald-200 border-emerald-700/50"
          : "bg-amber-950/40 text-amber-200 border-amber-700/40"
      }`}
    >
      <div className="px-4 py-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5">
          {sovereign
            ? <ShieldCheck className="w-3.5 h-3.5" />
            : <ShieldAlert className="w-3.5 h-3.5" />}
          <span data-testid="sov-mode-label">{info.label}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 normal-case tracking-normal text-[10px]">
          {storage.is_local ? <HardDrive className="w-3 h-3" /> : <Cloud className="w-3 h-3" />}
          <span className="opacity-80">Stockage:</span>
          <code className="font-mono text-[10px]" data-testid="sov-storage-name">{storage.backend}</code>
        </span>
        <span className="inline-flex items-center gap-1.5 normal-case tracking-normal text-[10px]">
          <Server className="w-3 h-3" />
          <span className="opacity-80">DB:</span>
          <code className="font-mono text-[10px]" data-testid="sov-db-name">{info.database?.kind}</code>
        </span>
        <span
          data-testid="sov-multipod-pill"
          title={`WS pub/sub: ${runtime.ws_pubsub || "?"} · Rate limit: ${runtime.rate_limit || "?"}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.15em] normal-case ${
            multiPod
              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
              : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
          }`}
        >
          <Radio className="w-3 h-3" />
          <span className="tracking-[0.15em] uppercase">
            {multiPod ? "Multi-pod" : "Mono-pod"}
          </span>
        </span>
        <span className="inline-flex items-center gap-1 ml-auto">
          <Pill on={ext.llm_transcription_video} label="LLM" testid="sov-ext-llm" />
          <Pill on={ext.tomtom_traffic_routing}  label="Trafic" testid="sov-ext-traffic" />
          <Pill on={ext.sms_gateway}             label="SMS"    testid="sov-ext-sms" />
          <Pill on={ext.email_gateway}           label="Email"  testid="sov-ext-email" />
        </span>
      </div>
    </div>
  );
};
